/**
 * `billing-prompt` — tiny observable store for surfacing the 402 upsell
 * dialog from anywhere in the app.
 *
 * Lifecycle:
 *   1. A query or mutation returns a `FetchError` with `status === 402`.
 *   2. The `QueryCache` / `MutationCache` error handler on the root
 *      `QueryClient` calls `billingPrompt.open({ feature, meta })`.
 *   3. A global `<UpsellDialog />` mounted in `_app` subscribes via
 *      `useBillingPrompt()` and renders.
 *
 * Implementation choice: `useSyncExternalStore` instead of `zustand`.
 * Rationale: we already have React 19, the API is stable (non-
 * experimental since React 18), and we'd otherwise be pulling in
 * zustand for a single store that has exactly two fields.
 * `04-data-layer.md` explicitly calls out "avoids adding a new dep".
 *
 * Stage 5 ships the `<UpsellDialog />`. Stage 4 only needs the store
 * + the hook so the 402 hand-off is wired end-to-end.
 */
import { useSyncExternalStore } from 'react';

/**
 * The "reason" surfaced alongside the upsell dialog. Matches the
 * `meta.feature` field on `apps/api`'s 402 responses (e.g.
 * `credits`, `max_on_demand_per_month`, `max_concurrent_snapshots`,
 * `api_access`).
 */
export interface BillingPromptState {
  open: boolean;
  feature: string | null;
  /**
   * Raw `error.meta` from the 402 response. Kept so the dialog can
   * surface the numeric limit / current usage without having to
   * recompute it.
   */
  meta: Record<string, unknown> | null;
  /** `meta.upgrade_url` if the API provided one. */
  upgradeUrl: string | null;
}

type Listener = () => void;

const CLOSED_STATE: BillingPromptState = {
  open: false,
  feature: null,
  meta: null,
  upgradeUrl: null,
};

let state: BillingPromptState = CLOSED_STATE;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

function extractUpgradeUrl(meta: Record<string, unknown> | null): string | null {
  if (!meta) return null;
  const v = meta.upgrade_url;
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export const billingPrompt = {
  /**
   * Open the upsell. Called by the query-cache error handler on every
   * 402. Idempotent — repeated calls with the same feature don't
   * re-emit (avoids a subscription storm when three queries fail
   * simultaneously).
   */
  open(payload: { feature?: string | null; meta?: Record<string, unknown> | null }): void {
    const feature = payload.feature ?? null;
    const meta = payload.meta ?? null;
    if (state.open && state.feature === feature) return;
    state = {
      open: true,
      feature,
      meta,
      upgradeUrl: extractUpgradeUrl(meta),
    };
    emit();
  },
  close(): void {
    if (!state.open) return;
    state = CLOSED_STATE;
    emit();
  },
  /** Primarily for tests. */
  reset(): void {
    state = CLOSED_STATE;
    listeners.clear();
  },
  /** Non-reactive getter — useful in event handlers. */
  get(): BillingPromptState {
    return state;
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
} as const;

/**
 * React hook. Use in any component that needs to render against the
 * upsell state. Snapshot is stable across renders as long as no open/
 * close happens, which keeps consumers cheap.
 */
export function useBillingPrompt(): BillingPromptState {
  return useSyncExternalStore(
    (fn) => billingPrompt.subscribe(fn),
    () => state,
    () => CLOSED_STATE,
  );
}
